// Twelve Data: the Canadian market source. Listings and symbol search are public (no key); quotes for TSX,
// TSX Venture, CSE and Cboe Canada need the Grow plan. CORS allows browser calls (verified 2026-09-17).
import { t } from '@/i18n';
import type { Quote, SymbolListing } from '@/types';
import { MarketError, marketFetch } from './errors';
import type { SymbolHit } from './finnhub';

const BASE = 'https://api.twelvedata.com';
const LABEL = 'Twelve Data';
export const CA_EXCHANGES = ['TSX', 'TSXV', 'CSE', 'NEO'] as const;

function errorFrom(json: { code?: number; message?: string }): MarketError {
  const code = json.code ?? 0;
  const detail = json.message ? `: ${json.message.slice(0, 160)}` : '';
  if (code === 401 || code === 403) return new MarketError('invalid_key', t('err.invalidKey', { label: LABEL, status: code, detail }), code);
  if (code === 429) return new MarketError('rate_limited', t('err.rateLimited', { label: LABEL }), code);
  if (code === 400 || code === 404) return new MarketError('not_found', t('err.notFound', { label: LABEL }), code);
  return new MarketError('upstream', t('err.upstream', { label: LABEL, status: code, detail }), code);
}

async function getJson<T>(path: string, params: Record<string, string>, apiKey?: string, signal?: AbortSignal): Promise<T> {
  const url = new URL(`${BASE}${path}`);
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  if (apiKey) url.searchParams.set('apikey', apiKey);
  const res = await marketFetch(url.toString(), { signal }, LABEL);
  const json = (await res.json()) as { status?: string; code?: number; message?: string };
  // Twelve Data reports most failures as HTTP 200 with a status:"error" body.
  if (json && json.status === 'error') throw errorFrom(json);
  return json as T;
}

export interface TwelveQuote {
  currency?: string;
  open?: string | number;
  high?: string | number;
  low?: string | number;
  close?: string | number;
  previous_close?: string | number;
  change?: string | number;
  percent_change?: string | number;
  timestamp?: number;
  is_market_open?: boolean;
}

const num = (v: string | number | undefined): number | undefined => {
  const n = typeof v === 'number' ? v : v != null ? Number(v) : NaN;
  return Number.isFinite(n) ? n : undefined;
};

/** Pure; exported for tests. */
export function parseTwelvedataQuote(symbol: string, q: TwelveQuote): Quote {
  const price = num(q.close);
  if (price == null || price <= 0) throw new MarketError('not_found', t('err.noQuote', { label: LABEL, symbol }));
  return {
    key: `eq:CA:${symbol.toUpperCase()}`,
    price,
    currency: (q.currency ?? 'CAD').toUpperCase(),
    asOf: q.timestamp ? q.timestamp * 1000 : Date.now(),
    change: num(q.change),
    changePct: num(q.percent_change),
    previousClose: num(q.previous_close),
    open: num(q.open),
    high: num(q.high),
    low: num(q.low),
    source: 'twelvedata',
    freshness: q.is_market_open ? 'realtime' : 'close',
    stale: false,
  };
}

export async function twelvedataQuote(symbol: string, mic: string | undefined, apiKey: string, signal?: AbortSignal): Promise<Quote> {
  const params: Record<string, string> = { symbol: symbol.toUpperCase() };
  if (mic) params.mic_code = mic;
  return parseTwelvedataQuote(symbol, await getJson<TwelveQuote>('/quote', params, apiKey, signal));
}

/** Verifies a key against a TSX blue chip; a Basic (free) key fails here with the plan message. */
export async function verifyTwelvedata(apiKey: string): Promise<string> {
  const q = await twelvedataQuote('RY', 'XTSE', apiKey);
  return `RY ${q.price.toFixed(2)} ${q.currency}`;
}

interface Row { symbol?: string; name?: string; type?: string; mic_code?: string; currency?: string }

/** Every stock and ETF on the four Canadian venues (~7,500 rows), no key needed. Rows repeat across venues; see symbolIndex.dedupeCanadian. */
export async function twelvedataListings(signal?: AbortSignal): Promise<SymbolListing[]> {
  const pulls = CA_EXCHANGES.flatMap((exchange) => [
    getJson<{ data?: Row[] }>('/stocks', { exchange }, undefined, signal),
    getJson<{ data?: Row[] }>('/etfs', { exchange }, undefined, signal).then((j) => ({ data: (j.data ?? []).map((r) => ({ ...r, type: 'ETF' })) })),
  ]);
  const results = await Promise.allSettled(pulls);
  const rows: SymbolListing[] = [];
  for (const r of results) {
    if (r.status !== 'fulfilled') continue;
    for (const row of r.value.data ?? []) if (row.symbol && row.name) rows.push({ symbol: row.symbol, name: row.name, type: row.type ?? 'Common Stock', mic: row.mic_code ?? '', currency: row.currency ?? 'CAD' });
  }
  if (rows.length === 0) {
    const failure = results.find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
    if (failure) throw failure.reason;
  }
  return rows;
}

/** Remote fallback while the local index loads; public endpoint, Canadian listings only. */
export async function twelvedataSearch(query: string, signal?: AbortSignal): Promise<SymbolHit[]> {
  const json = await getJson<{ data?: { symbol: string; instrument_name: string; mic_code?: string; country?: string; currency?: string; instrument_type?: string }[] }>('/symbol_search', { symbol: query, outputsize: '30' }, undefined, signal);
  return (json.data ?? [])
    .filter((r) => r.country === 'Canada')
    .map((r) => ({ symbol: r.symbol, name: r.instrument_name, type: r.instrument_type ?? '', exchange: r.mic_code, mic: r.mic_code, market: 'CA' as const, currency: r.currency }));
}
